/**
 * Apple Sign In via the iOS native AuthenticationServices framework
 * (wrapped by @capacitor-community/apple-sign-in).
 *
 * Use the native flow on iOS (HIG requirement); fall back to the web OAuth
 * flow on every other platform. The plugin is dynamically imported so the
 * web bundle isn't bloated with native-only code.
 */
import { isCapacitor } from '../api/config';
import { getClientPlatform } from '../utils/platform';

export interface AppleNativeSignInResult {
  identityToken: string;
  authorizationCode?: string;
  /** Apple supplies the user's name only on the very first sign-in. */
  user?: {
    name?: { firstName?: string; lastName?: string };
    email?: string;
  } | null;
}

/** True when the platform supports the native Apple sign-in plugin. */
export function supportsAppleNativeSignIn(): boolean {
  return isCapacitor && getClientPlatform() === 'ios';
}

/**
 * Trigger the native Apple sign-in sheet. Resolves with the identity token
 * (and any first-sign-in user payload). Throws on cancel or error.
 *
 * @param clientId The iOS app's bundle identifier (used as the OAuth client ID).
 */
export async function triggerAppleNativeSignIn(clientId: string): Promise<AppleNativeSignInResult> {
  if (!supportsAppleNativeSignIn()) {
    throw new Error('Native Apple sign-in is only available on iOS.');
  }
  // Call the plugin directly via the Capacitor bridge instead of the npm
  // wrapper. The wrapper would require a dynamic import of a bare module
  // specifier, which the WebView cannot resolve at runtime — the native
  // plugin is registered by Capacitor regardless.
  type SignInWithApplePlugin = {
    authorize: (opts: {
      clientId: string;
      redirectURI: string;
      scopes?: string;
      state?: string;
      nonce?: string;
    }) => Promise<{
      response: {
        identityToken?: string | null;
        authorizationCode?: string | null;
        givenName?: string | null;
        familyName?: string | null;
        email?: string | null;
      };
    }>;
  };
  const cap = (window as unknown as {
    Capacitor?: { Plugins?: { SignInWithApple?: SignInWithApplePlugin } };
  }).Capacitor;
  const SignInWithApple = cap?.Plugins?.SignInWithApple;
  if (!SignInWithApple) {
    throw new Error('Apple sign-in plugin is not registered on the Capacitor bridge.');
  }

  const response = await SignInWithApple.authorize({
    clientId,
    redirectURI: 'https://app.panluma.ai/auth/callback/apple', // unused on native, required by plugin
    scopes: 'email name',
    state: '', // not used on native — Apple's framework handles security
  });

  const r = response.response;
  if (!r?.identityToken) {
    throw new Error('Apple sign-in returned no identity token.');
  }
  const givenName = r.givenName ?? undefined;
  const familyName = r.familyName ?? undefined;
  return {
    identityToken: r.identityToken,
    authorizationCode: r.authorizationCode ?? undefined,
    user: givenName || familyName || r.email
      ? {
          name: { firstName: givenName, lastName: familyName },
          email: r.email ?? undefined,
        }
      : null,
  };
}
